"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "@/lib/useReducedMotion";
import type { Testimonial } from "@/lib/types";

const GAP = 16; // px between cards (matches gap-4)
const SPEED = 0.035; // px per ms of auto-scroll

export const isFile = (u: string) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(u);

export function embedUrl(u: string) {
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0`;
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null; // anything else is never dropped into an iframe
}

type VideoItem = { id: Testimonial["id"]; name: string; course?: string; src: string; embed: string | null };

function VideoCard({ v, active }: { v: VideoItem; active: boolean }) {
  // Only give the <video> its source once the card is near the screen: with preload="metadata" the browser
  // otherwise fetches data for every video at page load, competing with the hero image and scripts.
  const boxRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || v.embed) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNear(true); io.disconnect(); } }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [v.embed]);
  return (
    <figure className="w-[290px] shrink-0 sm:w-[360px]">
      <div
        ref={boxRef}
        className={`aspect-video overflow-hidden rounded-2xl bg-black shadow-sm ring-2 transition ${active ? "ring-brand-600" : "ring-transparent"}`}
      >
        {v.embed ? (
          <iframe
            src={v.embed}
            title={`${v.name} - video testimonial`}
            loading="lazy"
            allow="encrypted-media; picture-in-picture; fullscreen"
            className="h-full w-full border-0"
          />
        ) : (
          <video src={near ? v.src : undefined} controls controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} preload="metadata" className="h-full w-full" />
        )}
      </div>
      <figcaption className="mt-2.5 px-1">
        <span className={`block truncate text-sm font-bold ${active ? "he-brand-text" : ""}`}>{v.name}</span>
        {v.course && <span className="he-soft block truncate text-xs">{v.course}</span>}
      </figcaption>
    </figure>
  );
}

/**
 * Every video testimonial in one strip under the quotes. It drifts sideways on its own; when the testimonial being
 * shown has a video, the strip glides to that video and highlights it, otherwise it just keeps scrolling.
 * Hovering the strip stops it (so a video can be watched) - `onHover` lets the quote carousel pause too.
 */
export function H9VoiceVideos({
  testimonials,
  activeId,
  onHover,
}: {
  testimonials: Testimonial[];
  activeId: Testimonial["id"] | null;
  onHover: (hovering: boolean) => void;
}) {
  const reduce = useReducedMotion();
  const items = useMemo<VideoItem[]>(
    () =>
      testimonials
        .filter((t) => !!t.video && (isFile(t.video) || embedUrl(t.video)))
        .map((t) => ({ id: t.id, name: t.name, course: t.course, src: t.video as string, embed: isFile(t.video as string) ? null : embedUrl(t.video as string) })),
    [testimonials],
  );

  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const offset = useRef(0);
  const hovering = useRef(false);
  const activeIdx = items.findIndex((v) => v.id === activeId);
  const activeRef = useRef(activeIdx);
  useEffect(() => {
    activeRef.current = activeIdx;
  }, [activeIdx]);
  const [loopable, setLoopable] = useState(false); // more videos than fit → duplicate the list and scroll endlessly

  // do the videos overflow the visible width?
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const measure = () => {
      const card = window.innerWidth >= 640 ? 360 : 290;
      setLoopable(items.length * (card + GAP) - GAP > el.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  useEffect(() => {
    const t = track.current;
    if (!t) return;
    if (!loopable) {
      t.style.transform = "";
      offset.current = 0;
      return;
    }
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 64);
      last = now;
      const half = t.scrollWidth / 2; // width of one full copy of the list (+ its trailing gap)
      const idx = activeRef.current;
      if (idx >= 0) {
        // glide to the active testimonial's video (nearest of the two copies)
        const card = t.children[idx] as HTMLElement | undefined;
        if (card) {
          const target = card.offsetLeft;
          const goal = Math.abs(target + half - offset.current) < Math.abs(target - offset.current) ? target + half : target;
          offset.current += (goal - offset.current) * Math.min(1, dt * 0.006);
        }
      } else if (!hovering.current && !reduce) {
        offset.current += SPEED * dt;
      }
      if (half > 0 && offset.current >= half) offset.current -= half;
      t.style.transform = `translate3d(${-offset.current}px,0,0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loopable, reduce, items.length]);

  if (items.length === 0) return null;
  const list = loopable ? [...items, ...items] : items;

  return (
    <div className="min-w-0 max-w-full lg:mt-8">
      <div>
        <span className="he-kicker">Hear it from them</span>
        <h3 className="he-title mt-2 text-xl leading-tight sm:text-2xl">Video stories from our learners</h3>
      </div>
      <div
        ref={viewport}
        className="mt-6 w-full max-w-full overflow-hidden"
        onMouseEnter={() => {
          hovering.current = true;
          onHover(true);
        }}
        onMouseLeave={() => {
          hovering.current = false;
          onHover(false);
        }}
      >
        <div ref={track} className="flex will-change-transform" style={{ gap: GAP, paddingRight: loopable ? GAP : 0 }}>
          {list.map((v, k) => (
            <VideoCard key={`${v.id}-${k}`} v={v} active={v.id === activeId && k < items.length} />
          ))}
        </div>
      </div>
    </div>
  );
}
